'use client';

import { publicClient } from '@/api/client/public.client';
import {
  useCreatorSeries,
  useCreatorShorts,
  useCreatorVideos,
} from '@/api/queries/creators.queries';
import { usePublicVideos } from '@/api/queries/freemium.queries';
import { queryKeys } from '@/api/query-keys';
import { CarouselRow } from '@/app/(platform)/_home/_components/carousel-row';
import { MediaPreviewModal } from '@/app/(platform)/_home/_components/media-preview-modal';
import { ONLY_ON_TITLE_LABEL, OnlyOnTitle } from '@/app/(platform)/_home/_components/only-on-title';
import { useSeriesInfoModal } from '@/app/(platform)/series/_components/SeriesInfoModal/use-series-info-modal';
import { PageShell } from '@/components/layout/page-shell';
import { SeriesCardContainer } from '@/components/series/series-card-container';
import { VideoCardContainer } from '@/components/video/video-card-container';
import { ShortCardContainer } from '@/components/video/short-card-container';
import { CARD_IMAGE_SIZES } from '@/shared/constants/card-image-sizes';
import { shuffleArray } from '@/shared/utils/array';
import { getChoosePlanHrefForCreator, getCreatorRoute } from '@/shared/utils/routes';
import type { Creator, Series, Video } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { CreatorFeaturedVideo } from '../CreatorFeaturedVideo';
import { CreatorGlobeSection } from '../CreatorGlobeSection';
import { CreatorMiniPassportContainer } from '../CreatorMiniPassportContainer';
import { CreatorTicketBandContainer } from '../CreatorTicketBandContainer';
import { pickExclusiveVideos } from './shared/exclusive-videos';
import { ShortsGridSkeleton, VideoGridSkeleton } from './shared/TabSkeletons';

// The exclusives row filters this newest window client-side (no backend filter yet), so the
// fetch is wider than the rows it feeds; the rows keep their previous lengths.
const VIDEO_WINDOW = 60;
const LATEST_ROW_LIMIT = 15;
const LOCKED_ROW_LIMIT = 20;

interface CreatorHomeTabProps {
  creator: Creator;
  handler: string;
  isGuest?: boolean;
}

export function CreatorHomeTab({ creator, handler, isGuest }: CreatorHomeTabProps) {
  // ── Auth: authenticated endpoints ──
  const { data: videosData, isLoading: videosLoading } = useCreatorVideos(
    isGuest ? undefined : creator.id,
    { sort_by: 'newest', per_page: VIDEO_WINDOW }
  );
  const { data: shortsData, isLoading: shortsLoading } = useCreatorShorts(
    isGuest ? undefined : creator.id,
    { sort_by: 'newest' }
  );
  const { data: seriesData } = useCreatorSeries(isGuest ? undefined : creator.id, {
    sort_by: 'newest',
  });
  const { openInfo: openSeriesInfo, modal: seriesInfoModal } = useSeriesInfoModal('series_list');

  // ── Public videos = source of truth for FREE playable videos (all auth states) ──
  const { data: publicData, isLoading: publicLoading } = usePublicVideos({
    creators: creator.id,
  });
  // ── Guest: map videos = ALL videos for the channel (gives us locked ones) ──
  const mapFilters = useMemo(
    () => ({ creators: creator.id, per_page: VIDEO_WINDOW }),
    [creator.id]
  );
  const { data: mapResponse, isLoading: mapLoading } = useQuery({
    queryKey: queryKeys.videos.mapList(mapFilters),
    queryFn: () => publicClient.getMapVideos(mapFilters),
    staleTime: 1000 * 60 * 5,
    enabled: !!isGuest,
  });
  // 3) Map videos with types=shorts for locked shorts carousel
  const shortsMapFilters = useMemo(() => ({ creators: creator.id, types: 'shorts' }), [creator.id]);
  const { data: mapShortsResponse, isLoading: mapShortsLoading } = useQuery({
    queryKey: queryKeys.videos.mapList(shortsMapFilters),
    queryFn: () => publicClient.getMapVideos(shortsMapFilters),
    staleTime: 1000 * 60 * 5,
    enabled: !!isGuest,
  });

  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);
  const featuredRef = useRef<HTMLDivElement | null>(null);
  const passportRef = useRef<HTMLDivElement | null>(null);

  // ── Guest: cross-reference to split free vs locked ──
  const freeVideos = useMemo(() => publicData?.videos || [], [publicData?.videos]);

  const freeUuids = useMemo(() => new Set(freeVideos.map((v) => v.uuid)), [freeVideos]);

  const lockedVideosRaw = useMemo(() => {
    const items = mapResponse?.videos;
    if (!items) return [];
    return items.filter((v) => !freeUuids.has(v.uuid));
  }, [mapResponse, freeUuids]);

  const lockedShorts = useMemo(() => mapShortsResponse?.videos ?? [], [mapShortsResponse]);

  // ── Resolve data based on auth state ──
  const authVideos = useMemo(() => videosData?.data || [], [videosData?.data]);
  const isLoading = isGuest
    ? publicLoading || mapLoading || mapShortsLoading
    : videosLoading || shortsLoading || publicLoading;

  // Featured video: latest published for guests (free or locked), first video for auth
  const featuredVideo = isGuest ? mapResponse?.videos?.[0] : authVideos[0];
  const featuredIsFree = !!(isGuest && featuredVideo && freeUuids.has(featuredVideo.uuid));

  // Remaining free videos (after featured)
  const remainingFreeVideos = useMemo(() => {
    if (!featuredVideo) return freeVideos;
    return freeVideos.filter((v) => v.uuid !== featuredVideo.uuid);
  }, [freeVideos, featuredVideo]);

  // Locked videos excluding featured (if featured fell back to a locked video)
  const lockedVideos = useMemo(
    () => lockedVideosRaw.filter((v) => v.uuid !== featuredVideo?.uuid).slice(0, LOCKED_ROW_LIMIT),
    [lockedVideosRaw, featuredVideo]
  );

  // Auth: remaining videos after featured
  const authLatestVideos = useMemo(
    () => authVideos.filter((v) => v.uuid !== featuredVideo?.uuid).slice(0, LATEST_ROW_LIMIT),
    [authVideos, featuredVideo]
  );

  const exclusiveVideos = useMemo(
    () => pickExclusiveVideos(isGuest ? lockedVideosRaw : authVideos, featuredVideo?.uuid),
    [isGuest, lockedVideosRaw, authVideos, featuredVideo]
  );

  const creatorSeries = useMemo(() => seriesData?.data ?? [], [seriesData?.data]);

  const randomShorts = useMemo(() => {
    const shorts = isGuest ? [] : shortsData?.data || [];
    return shuffleArray(shorts);
  }, [isGuest, shortsData?.data]);

  const handleOpenPreview = useCallback((video: Video) => setPreviewVideo(video), []);
  const handleClosePreview = useCallback(() => setPreviewVideo(null), []);

  const renderFreeVideo = useCallback(
    (video: Video) => <VideoCardContainer video={video} sizes={CARD_IMAGE_SIZES.landscapeLarge} />,
    []
  );

  const renderAuthVideo = useCallback(
    (video: Video) => <VideoCardContainer video={video} onOpenPreview={handleOpenPreview} />,
    [handleOpenPreview]
  );

  const renderAuthVideoLarge = useCallback(
    (video: Video) => (
      <VideoCardContainer
        video={video}
        onOpenPreview={handleOpenPreview}
        sizes={CARD_IMAGE_SIZES.landscapeLarge}
      />
    ),
    [handleOpenPreview]
  );

  const choosePlanHref = getChoosePlanHrefForCreator(handler);

  const renderLockedVideo = useCallback(
    (video: Video) => <VideoCardContainer video={video} locked href={choosePlanHref} />,
    [choosePlanHref]
  );

  const renderLockedVideoLarge = useCallback(
    (video: Video) => (
      <VideoCardContainer
        video={video}
        locked
        href={choosePlanHref}
        sizes={CARD_IMAGE_SIZES.landscapeLarge}
      />
    ),
    [choosePlanHref]
  );

  const renderLockedShort = useCallback(
    (video: Video) => <ShortCardContainer video={video} locked href={choosePlanHref} hideCreator />,
    [choosePlanHref]
  );

  const renderShort = useCallback(
    (video: Video) => <ShortCardContainer video={video} hideCreator />,
    []
  );

  const renderSeries = useCallback(
    (item: Series) => <SeriesCardContainer series={item} onOpenInfo={openSeriesInfo} />,
    [openSeriesInfo]
  );

  const getKey = useCallback((video: Video) => video.uuid || String(video.id), []);
  const getSeriesKey = useCallback((item: Series) => item.uuid || String(item.id), []);

  if (isLoading) {
    return (
      <PageShell className="space-y-8">
        <div>
          <div className="animate-pulse rounded overflow-hidden bg-white/5">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-[55%] aspect-video bg-white/10" />
              <div className="flex-1 p-6 space-y-3">
                <div className="h-5 bg-white/10 rounded w-3/4" />
                <div className="h-4 bg-white/10 rounded w-full" />
                <div className="h-4 bg-white/10 rounded w-2/3" />
                <div className="h-8 bg-white/10 rounded w-24 mt-4" />
              </div>
            </div>
          </div>
        </div>
        <VideoGridSkeleton count={6} />
        {!isGuest && <ShortsGridSkeleton count={6} />}
        <div
          className="w-full animate-pulse rounded bg-white/5"
          style={{ height: '600px', minHeight: '400px' }}
        />
      </PageShell>
    );
  }

  const videosHref = `${getCreatorRoute(handler)}/videos`;

  // ── Guest layout ──
  if (isGuest) {
    return (
      <div className="flex flex-col gap-12 pt-6">
        {/* Featured: latest published video — locked ones render the unlock CTA */}
        {featuredVideo && (
          <div ref={featuredRef}>
            <CreatorFeaturedVideo video={featuredVideo} isFree={featuredIsFree} handler={handler} />
          </div>
        )}

        {remainingFreeVideos.length > 0 && (
          <PageShell spacing="none">
            <CarouselRow
              title="Free Videos"
              href={videosHref}
              items={remainingFreeVideos}
              renderItem={renderFreeVideo}
              getKey={getKey}
              size="large"
              contained
            />
          </PageShell>
        )}

        {exclusiveVideos.length > 0 && (
          <PageShell spacing="none">
            <CarouselRow
              title={ONLY_ON_TITLE_LABEL}
              titleNode={OnlyOnTitle}
              size="large"
              items={exclusiveVideos}
              renderItem={renderLockedVideoLarge}
              getKey={getKey}
              contained
            />
          </PageShell>
        )}

        {lockedVideos.length > 0 && (
          <PageShell spacing="none">
            <CarouselRow
              title={`More from ${creator.name}`}
              items={lockedVideos}
              renderItem={renderLockedVideo}
              getKey={getKey}
              contained
            />
          </PageShell>
        )}

        {lockedShorts.length > 0 && (
          <PageShell spacing="none">
            <CarouselRow
              title="Shorts"
              items={lockedShorts}
              renderItem={renderLockedShort}
              getKey={getKey}
              aspectRatio="portrait"
              size="small"
              contained
            />
          </PageShell>
        )}

        <CreatorGlobeSection handler={handler} creatorId={creator.id} />

        <PageShell spacing="none" className="mb-8">
          <div ref={passportRef}>
            <CreatorTicketBandContainer creator={creator} handler={handler} isGuest />
          </div>
        </PageShell>

        {featuredVideo && (
          <CreatorMiniPassportContainer
            creator={creator}
            handler={handler}
            featuredRef={featuredRef}
            passportRef={passportRef}
          />
        )}
      </div>
    );
  }

  // ── Auth layout ──
  return (
    <div className="flex flex-col gap-12 pt-6">
      {featuredVideo && <CreatorFeaturedVideo video={featuredVideo} canWatch handler={handler} />}

      {remainingFreeVideos.length > 0 && (
        <PageShell spacing="none">
          <CarouselRow
            title="Free Videos"
            href={videosHref}
            items={remainingFreeVideos}
            renderItem={renderAuthVideoLarge}
            getKey={getKey}
            size="large"
            contained
          />
        </PageShell>
      )}

      {exclusiveVideos.length > 0 && (
        <PageShell spacing="none">
          <CarouselRow
            title={ONLY_ON_TITLE_LABEL}
            titleNode={OnlyOnTitle}
            size="large"
            items={exclusiveVideos}
            renderItem={renderAuthVideoLarge}
            getKey={getKey}
            contained
          />
        </PageShell>
      )}

      {authLatestVideos.length > 0 && (
        <PageShell spacing="none">
          <CarouselRow
            title="Latest Videos"
            href={videosHref}
            items={authLatestVideos}
            renderItem={renderAuthVideo}
            getKey={getKey}
            contained
          />
        </PageShell>
      )}

      {creatorSeries.length > 0 && (
        <PageShell spacing="none">
          <CarouselRow
            title="Series"
            href={`${getCreatorRoute(handler)}/series`}
            items={creatorSeries}
            renderItem={renderSeries}
            getKey={getSeriesKey}
            contained
          />
        </PageShell>
      )}

      {randomShorts.length > 0 && (
        <PageShell spacing="none">
          <CarouselRow
            title="Shorts"
            href={`${getCreatorRoute(handler)}/shorts`}
            items={randomShorts}
            renderItem={renderShort}
            getKey={getKey}
            aspectRatio="portrait"
            size="small"
            contained
          />
        </PageShell>
      )}

      <CreatorGlobeSection handler={handler} creatorId={creator.id} />

      <PageShell spacing="none" className="mb-8">
        <CreatorTicketBandContainer creator={creator} handler={handler} isGuest={false} />
      </PageShell>

      {previewVideo && <MediaPreviewModal video={previewVideo} onClose={handleClosePreview} />}
      {seriesInfoModal}
    </div>
  );
}
